import { authAppUrl } from "@/config/authAppUrl";
import { slugFromHost } from "@/contexts/TenantContext";
import { LazoPaths } from "@/enums/LazoPaths";
import { isWorkspaceHost } from "@/utils/RedirectUtils";

/**
 * Where to send someone who is not signed in (LAZ-2464).
 *
 * One decision point for every seam that currently sends people to the legacy login. Keeping it
 * pure — no `window`, no React, no network — is what makes the host-shape and query-param rules
 * testable without a browser, and it is the only place the auth-app URL is assembled.
 */

export interface LoginRedirectInput {
  /** Resolved feature flag. False (the fail-closed default) keeps the legacy login. */
  newLoginEnabled: boolean;
  /** `window.location.host` — may carry a port in dev. */
  host: string;
  /** `environment.env` (DEV / QA / PROD); selects the host shape the slug is parsed from. */
  env: string;
  /** Absolute URL the person was trying to reach, returned to after sign-in. */
  currentUrl: string;
}

/**
 * Paths that are entry points rather than destinations. Sending someone "back" to one of these
 * after sign-in returns them to the door they just came through — and for the two login routes,
 * re-triggers the very handoff that just completed.
 */
const ENTRY_POINT_PATHS = new Set<string>(["/", LazoPaths.LOGIN, LazoPaths.NEW_LOGIN].map((path) => path.toLowerCase()));

/**
 * Whether `currentUrl` names a real destination worth returning to.
 *
 * Only the path is consulted: a query or fragment hanging off `/login` describes how the person
 * reached the login screen, not where they were headed, so `/login?next=x` is still an entry point.
 * A query on a real path (`/tasks?filter=open`) is part of the destination and is preserved by the
 * caller.
 *
 * An unparseable URL is treated as no destination — a `returnTo` that cannot be parsed here is one
 * the auth app cannot validate either. Note this is an emptiness check, NOT an origin check: an
 * off-origin `https://evil.com/tasks` is still forwarded, exactly as before this rule existed.
 * Open-redirect defense deliberately stays in the auth app, which validates `returnTo` against its
 * own *.lazo.one allow-list at the point the redirect is actually performed; duplicating it here
 * would give two places to keep in sync and a false sense that this side is authoritative.
 */
function hasReturnableDestination(currentUrl: string): boolean {
  let pathname: string;
  try {
    ({ pathname } = new URL(currentUrl));
  } catch {
    return false;
  }

  // Strip trailing slashes so `/login/` is judged the same as `/login`, and collapse what is left
  // of a slash-only path (`/`, `//`) to a single `/` — `https://app.lazo.one//` is still the root.
  // Lower-cased because the router matches routes case-insensitively, so `/LOGIN` reaches the same
  // login screen and is just as much an entry point.
  const normalized = pathname.replace(/\/+$/, "").toLowerCase() || "/";
  return !ENTRY_POINT_PATHS.has(normalized);
}

/**
 * Resolves the destination. With the flag off this is `LazoPaths.LOGIN`, byte-identical to today's
 * behaviour; with it on, the auth app carrying `returnTo` and — only from a workspace subdomain —
 * `company`.
 */
export function resolveLoginRedirect(input: LoginRedirectInput): string {
  const { newLoginEnabled, host, env, currentUrl } = input;

  if (!newLoginEnabled) {
    return LazoPaths.LOGIN;
  }

  // `returnTo` is omitted rather than emptied when there is nothing to return to: an empty param
  // is a claim about a destination of "", which the auth app would then have to defend against —
  // the same reasoning that keeps `company` absent on the root app.
  const params = new URLSearchParams();
  if (hasReturnableDestination(currentUrl)) {
    params.set("returnTo", currentUrl);
  }

  // Both checks are needed, and neither is sufficient alone:
  //
  //   slugFromHost only counts labels. It handles the two live shapes — prod `<slug>.lazo.one`
  //   (3 labels) and QA `<slug>.qa.lazo.one` (4, its own subzone) — and returns "" at either
  //   apex, but it has no notion of a root app: `app.lazo.one` yields "app" and `www.lazo.one`
  //   yields "www".
  //
  //   isWorkspaceHost knows the root-app labels (app/qa/dev/www/localhost) but decides by
  //   blocklist, so it reads a bare `lazo.one` as a workspace.
  //
  // Requiring both means a company is named only when the host really is a workspace subdomain
  // AND a slug can actually be parsed from it.
  const slug = isWorkspaceHost(host) ? slugFromHost(host, env) : "";
  if (slug) {
    // A hint only. The auth app validates it against the signed-in person's own workspaces before
    // acting on it — this side never implies the slug has been authorized.
    params.set("company", slug);
  }

  // On the root app with no destination both params are absent, so emit a clean `/` rather than a
  // bare trailing `?`.
  const query = params.toString();
  return query ? `${authAppUrl()}/?${query}` : `${authAppUrl()}/`;
}
